import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../hooks/useLanguage';
import { CornerBrackets } from './CornerBrackets';
import { CyberButton } from './CyberButton';
import { Sparkles, Code, Network } from 'lucide-react';
import { ProjectModal } from './ProjectModal';
import { ContactModal } from './ContactModal';
interface PricingSectionProps {
  onModalStateChange?: (isOpen: boolean) => void;
}
export function PricingSection({
  onModalStateChange
}: PricingSectionProps) {
  const {
    t,
    language
  } = useLanguage();
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(isProjectModalOpen || isContactModalOpen);
    }
  }, [isProjectModalOpen, isContactModalOpen, onModalStateChange]);
  const pricingCards = [{
    icon: Sparkles,
    title: t.pricing.cards.website.title,
    price: t.pricing.cards.website.price,
    priceSubtext: t.pricing.cards.website.priceSubtext,
    description: t.pricing.cards.website.description,
    features: t.pricing.cards.website.features,
    cta: t.pricing.cards.website.cta,
    variant: 'cyan' as const,
    action: () => setIsProjectModalOpen(true)
  }, {
    icon: Code,
    title: t.pricing.cards.application.title,
    price: t.pricing.cards.application.price,
    priceSubtext: t.pricing.cards.application.priceSubtext,
    description: t.pricing.cards.application.description,
    features: t.pricing.cards.application.features,
    cta: t.pricing.cards.application.cta,
    variant: 'magenta' as const,
    featured: true,
    action: () => setIsProjectModalOpen(true)
  }, {
    icon: Network,
    title: t.pricing.cards.consulting.title,
    price: t.pricing.cards.consulting.price,
    priceSubtext: t.pricing.cards.consulting.priceSubtext,
    description: t.pricing.cards.consulting.description,
    features: t.pricing.cards.consulting.features,
    cta: t.pricing.cards.consulting.cta,
    variant: 'cyan' as const,
    action: () => setIsContactModalOpen(true)
  }];
  return <>
      <section className="py-24 bg-black/90 relative" data-id="element-215">
        <div className="max-w-7xl mx-auto px-4" data-id="element-216">
          {/* Section Header */}
          <div className="text-center mb-16" data-id="element-217">
            <h2 className="font-cyber text-4xl md:text-5xl font-bold text-white mb-4 tracking-wider" data-id="element-218">
              <span className="text-cyan" data-id="element-219">{t.pricing.title.split(' ')[0]}</span>{' '}
              {t.pricing.title.split(' ').slice(1).join(' ')}
            </h2>
            <p className="text-gray-500 uppercase tracking-widest text-sm" data-id="element-220">
              {t.pricing.subtitle}
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" data-id="element-221">
            {pricingCards.map((card, index) => {
            const Icon = card.icon;
            const borderColor = card.variant === 'cyan' ? 'border-cyan/50' : 'border-magenta/50';
            const iconBg = card.variant === 'cyan' ? 'bg-cyan/5' : 'bg-magenta/5';
            const iconBorder = card.variant === 'cyan' ? 'border-cyan/30' : 'border-magenta/30';
            const iconColor = card.variant === 'cyan' ? 'text-cyan' : 'text-magenta';
            const iconHoverBg = card.variant === 'cyan' ? 'group-hover:bg-cyan' : 'group-hover:bg-magenta';
            const titleHoverColor = card.variant === 'cyan' ? 'group-hover:text-cyan' : 'group-hover:text-magenta';
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} className={`group relative p-8 bg-dark-card border border-white/5 hover:${borderColor} transition-colors duration-300 flex flex-col ${card.featured ? 'lg:scale-105 border-magenta/30' : ''}`} data-id="element-222">
                  <CornerBrackets className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" color={card.variant} data-id="element-223" />

                  <div className="relative z-10 flex flex-col h-full" data-id="element-224">
                    {/* Icon */}
                    <div className={`w-12 h-12 mb-6 flex items-center justify-center border ${iconBorder} ${iconBg} ${iconColor} ${iconHoverBg} group-hover:text-black transition-all duration-300`} data-id="element-225">
                      <Icon size={24} data-id="element-226" />
                    </div>

                    {/* Title - Fixed Height */}
                    <h3 className={`font-cyber text-xl font-bold text-white mb-3 ${titleHoverColor} transition-colors min-h-[3.5rem] flex items-start`} data-id="element-227">
                      {card.title}
                    </h3>

                    {/* Price - Fixed Height */}
                    <div className="mb-4 min-h-[5rem]" data-id="element-228">
                      <div className={`font-cyber text-3xl font-bold ${iconColor} mb-1`} data-id="element-229">
                        {card.price}
                      </div>
                      {card.priceSubtext && <div className="text-xs text-gray-600 font-mono" data-id="element-230">
                          {card.priceSubtext}
                        </div>}
                    </div>

                    {/* Description - Fixed Height */}
                    <p className="text-gray-500 text-sm leading-relaxed mb-6 group-hover:text-gray-300 transition-colors min-h-[3rem]" data-id="element-231">
                      {card.description}
                    </p>

                    {/* Features - Flexible Height */}
                    <ul className="space-y-3 mb-8 flex-grow" data-id="element-232">
                      {card.features.map((feature, i) => <li key={i} className="flex items-start gap-3 text-sm text-gray-400" data-id="element-233">
                          <div className="relative flex h-2 w-2 mt-1.5 flex-shrink-0" data-id="element-234">
                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${card.variant === 'cyan' ? 'bg-cyan' : 'bg-magenta'} opacity-75 group-hover:opacity-100`} data-id="element-235"></span>
                            <span className={`relative inline-flex rounded-full h-2 w-2 ${card.variant === 'cyan' ? 'bg-cyan' : 'bg-magenta'}`} data-id="element-236"></span>
                          </div>
                          <span className="group-hover:text-gray-200 transition-colors" data-id="element-237">
                            {feature}
                          </span>
                        </li>)}
                    </ul>

                    {/* CTA Button - Always at Bottom */}
                    <div className="mt-auto" data-id="element-238">
                      <CyberButton variant={card.variant === 'cyan' ? 'primary' : 'secondary'} className="w-full text-xs py-3" onClick={card.action} data-id="element-239">
                        {card.cta}
                      </CyberButton>
                    </div>
                  </div>

                  {/* Featured Badge */}
                  {card.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-magenta/20 border border-magenta/50 backdrop-blur-sm" data-id="element-240">
                      <span className="text-[10px] font-cyber text-magenta tracking-widest" data-id="element-241">
                        {language === 'fr' ? 'POPULAIRE' : 'POPULAR'}
                      </span>
                    </div>}
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Modals */}
      <ProjectModal isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)} data-id="element-242" />
      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} data-id="element-243" />
    </>;
}